import ActionButton from "Common/Components/Buttons/ActionButton";
import ExportButton from "Common/Components/Buttons/ExportButton";
import ColumnVisibilityDropdown from "Common/Components/ColumnFilteringPopUp/ColumnFilteringPopUp";
import LoaderErrorWrapper from "Common/Components/LoaderErrorWrapper/LoaderErrorWrapper";
import ListingPageContainer from "Common/Components/PageContainer/ListingPageContainer";
import SearchBox from "Common/Components/SearchBox/SearchBox";
import UserManagementTableContainer from "Common/Tables/UserManagementFiltersTable/UserManagementTableContainer";
import { useEffect, useMemo, useState } from "react";
import { Card, Col, Row } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import { SortingState } from "@tanstack/react-table";
import { API_ENDPOINT } from "helpers/Endpoints/endpoints";
import { showCustomSwal, showError, showSuccess } from "helpers/swalHelper";
import Swal from "sweetalert2";
import NameWithDate from "Common/Components/NameWithDate.tsx/NameWithDate";
import { DynamicMenuItem } from "Common/Components/DynamicActionMenu/DynamicActionMenu";
import EditIcon from "@mui/icons-material/Edit";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchUserTypeList,
  updateUserTypeStatus,
  UserTypeExportsCSV,
} from "slices/UserManagement/UserType/thunk";
import { app_modules } from "helpers/apps_modules_list";
import { usePermission } from "hooks/usePermission";
import usePreservedTableState from "CustomHooks/usePreservedTableState";
import { TableKeys } from "Constants/TableKeys/TableKeys";
import { UserTypeConstant } from "Constants/UserTypeConstant";
import BlockIcon from '@mui/icons-material/Block';
const UserTypeList = () => {

  // Use the preserved table state hook
  const tableState = usePreservedTableState({
    key: TableKeys?.USER_MANAGEMENT?.USER_TYPE,
    defaults: {
      pageIndex: 0,
      pageSize: 10,
      search: "",
      searchInput: "",
      sorting: [],
      columnFilters: [],
      filterValue: [],
    },
  });

  const {
    pageIndex,
    pageSize,
    search,
    searchInput,
    sorting,
    columnFilters,
    filterValue: colValue,
    setPageIndex,
    setPageSize,
    setSearch,
    setSearchInput,
    setSorting,
    setColumnFilters,
    setFilterValue: setColValue,
    resetState,
  } = tableState;


  // const [pageIndex, setPageIndex] = useState(0);
  // const [pageSize, setPageSize] = useState(10);
  // const [columnFilters, setColumnFilters] = useState<any>([]);
  // const [sorting, setSorting] = useState<SortingState>([]);
  // const [searchInput, setSearchInput] = useState("");
  // const [search, setSearch] = useState("");
  // const [colValue, setColValue] = useState<any>();

  const sortField = sorting[0]?.id ?? "";
  const sortDirection = sorting[0]?.desc ? "desc" : "asc";
  const [tableInstance, setTableInstance] = useState<any>();
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();

  const { list, rowCount, loading, error } = useSelector((state: any) => state.userType);

  // Use permission hook for UserType
  const { canView, canCreate, canUpdate, canDelete } = usePermission(
    app_modules?.user_management?.app_name,
    app_modules?.user_management?.modules?.userType
  );

  useEffect(() => {
    dispatch(
      fetchUserTypeList({
        page: pageIndex + 1,
        pageSize,
        sort: sortField,
        order: sortField ? (sortDirection as "asc" | "desc") : "",
        search,
        filters: columnFilters,
      })
    );
  }, [dispatch, pageIndex, pageSize, sorting, search, columnFilters, colValue]);

  // Debounced search
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setSearch(searchInput);
    }, 500);
    return () => clearTimeout(delayDebounce);
  }, [searchInput]);

  // Handle status toggle
  const handleStatusToggle = async (userType: any) => {
    const updatedStatus = userType.is_active === true ? false : true;

    // Show loading modal
    showCustomSwal({
      title: "Updating...",
      text: "Please wait while we update the User Type status.",
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      await dispatch(updateUserTypeStatus(userType.user_type_id)).unwrap();

      showSuccess(
        `User Type ${
          updatedStatus ? "activated" : "deactivated"
        } successfully.`,
                "Status Updated",
      );

      dispatch(
        fetchUserTypeList({
          page: pageIndex + 1,
          pageSize,
          sort: sortField,
          order: sortField ? (sortDirection as "asc" | "desc") : "",
          search,
          filters: columnFilters,
        })
      );
    } catch (err: any) {
      const errorMessage =
        err?.error || err?.message || "Failed to update User Type status";
      showError(errorMessage, "Update Failed");
    }
  };


  const DynamicAction = canUpdate
    ? [
        {
          header: "Action",
          enableColumnFilter: false,
          enableSorting: false,
          cell: ({ row }: { row: any }) => {
            const isDisabled = UserTypeConstant.includes(row.original.user_type);
            const menuItems =  [
              {
                icon: EditIcon,
                label: "Edit",
                permission: canUpdate,
                onClick: (userType: any) => {
                  navigate(`/userType/${userType.user_type_id}`);
                },
                color: "primary",
              },
            ];
            return isDisabled ? <BlockIcon style={{ color: "red", fontSize: "1.2rem" }}/> : <DynamicMenuItem row={row.original} menuItems={menuItems} />;
          }
        },
      ]
    : [];

  // Table columns
  const columns = useMemo(
    () => [
      {
        header: "Sr. No.",
        accessorKey: "sr_no",
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: "User Type",
        accessorKey: "user_type",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: `${API_ENDPOINT?.USER_MANAGEMENT?.USER_TYPE?.FILTER_DROPDOWN("user_type")}`,
      },
      {
        header: "Status",
        accessorKey: "is_active",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => {
          const org = row.original;
          const isDisabled = UserTypeConstant.includes(org.user_type);
          console.log(isDisabled);
          console.log("org.user_type", org.user_type);
          console.log("UserTypeConstant", UserTypeConstant);
          return (
            <div
              style={{ fontSize: "1.5rem", cursor: isDisabled ? "not-allowed" : "pointer", opacity: isDisabled ? 0.5 : 1 }}
              onClick={() => !isDisabled && handleStatusToggle(org)}
            >
              <i
                className={`bi ${
                  org.is_active
                    ? "bi-toggle-on text-success"
                    : "bi-toggle-off text-danger"
                }`}
              />
            </div>
          );
        },
      },
      {
        header: "Created By/Date",
        accessorKey: "created_at",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => (
          <NameWithDate
            name={row.original.created_by_user?.username}
            date={row.original.created_at}
          />
        ),
      },
      {
        header: "Updated By/Date",
        accessorKey: "updated_at",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => (
          <NameWithDate
            name={row.original.updated_by_user?.username}
            date={row.original.updated_at}
          />
        ),
      },
      ...DynamicAction,
    ],
    [dispatch, handleStatusToggle, canUpdate, canDelete]
  );
  
  return (
    <>
      <ListingPageContainer>
        <Row id="invoiceList">
          <Col lg={12}>
            <Card className="shadow-none">
              <Card.Header>
                <Row className="align-items-center g-2">
                  <Col lg={3} className="me-auto">
                    <h6 className="card-title mb-0">User Type</h6>
                  </Col>
                  <Row className="align-items-center justify-content-between mt-1 px-0 g-3 flex-wrap">
                    {/* Search Box */}
                    <SearchBox
                      value={searchInput}
                      onChange={(e: any) => setSearchInput(e.target.value)}
                      placeholder="Search..."
                    />

                    {/* Column Visibility + Actions */}
                    <Col xs={12} md={12} lg={8} xl={6} className="">
                      <div className="d-flex flex-wrap align-items-center justify-content-end gap-2 w-100">
                        {/* Left side: Column Visibility */}
                        <div className="">
                          {tableInstance && (
                            <ColumnVisibilityDropdown
                              columns={tableInstance.getAllLeafColumns()}
                              excludedColumns={["action", "sr_no"]}
                            />
                          )}
                        </div>

                        <div className="hstack gap-2">
                          {canView && (
                            <ExportButton
                              onExport={async () => {
                                return await UserTypeExportsCSV({
                                  search,
                                  filters: columnFilters,
                                });
                              }}
                            />
                          )}
                        </div>

                        <div>
                          {canCreate && (
                            <ActionButton
                              label="Add User Type"
                              icon="bi bi-plus-circle"
                              navigateLink="/userType-form"
                              variant="primary"
                            />
                          )}
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Row>
              </Card.Header>
              <Card.Body className="mt-3">
                <LoaderErrorWrapper loading={loading} error={error}>
                <UserManagementTableContainer
                  columns={columns}
                  data={list || []}
                  totalRecords={rowCount}
                  pageIndex={pageIndex}
                  pageSize={pageSize}
                  onPageChange={setPageIndex}
                  onPageSizeChange={setPageSize}
                  columnFilters={columnFilters}
                  setColumnFilters={setColumnFilters}
                  colValue={setColValue}
                  sorting={sorting}
                  onSortingChange={setSorting}
                  manualSorting={true}
                  isBordered={false}
                  isPagination={true}
                  isGlobalFilter={false}
                  SearchPlaceholder="Search User Types..."
                  onTableReady={setTableInstance}
                />
                </LoaderErrorWrapper>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </ListingPageContainer>
    </>
  );
};

export default UserTypeList;
